import React, { useEffect, useState } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { fetchCaseRange, fetchReportGoals, fetchReportTasks } from "../../services/httpClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import { GoalsCompletedMetric } from "./charts-metrics/GoalsCompletedMetric";
import { TasksCompletedMetric } from "./charts-metrics/TasksCompletedMetric";
import { formatDateTimeToLocal } from "../../helpers/date";
import { GoalsOutstandingMetric } from "./charts-metrics/GoalsOutstandingMetric";
import { GoalsCompletedPercentageMetric } from "./charts-metrics/GoalsCompletedPercentageMetric";
import DataTable from "./ui/DataTable";
import { activeKey, reportRunning } from "./Reports";

const GoalsAndTasksTab = ({ buildPDF, iPad }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [selectedData, setSelectedData] = useState<any[] | null>(null);
  const [dataHeaders, setDataHeaders] = useState<any[] | null>();
  const [activeCard, setActiveCard] = useState<string>("Goals Outstanding");
  const [data, setData] = useState<any>();

  useEffect(() => {
    if (activeKey.value === "goals and tasks") {
      reportRunning.value = false;
      const start = new Date();
      start.setDate(start.getDate() - dateInterval);
      const startString = start.toISOString().slice(0, 10);
      const endString = new Date().toISOString().slice(0, 10);
      setStartDate(startString);
      setEndDate(endString);
      getData(startString, endString);
    }
  }, [activeKey.value]);

  const getData = async (start: string, end: string) => {
    const [caseRange, goals, tasks] = await Promise.all([getCaseRange(start, end), getGoals(start, end), getTasks(start, end)]);

    setData({
      caseRange,
      goals,
      tasks,
    });
    setIsLoading(false);
  };

  const getCaseRange = async (start: string, end: string) => {
    return fetchCaseRange(start, end)
      .then((res: any) => {
        const cases = res[0].data;
        return cases;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const getGoals = async (start: string, end: string) => {
    return fetchReportGoals(start, end)
      .then((res: any) => {
        const goalData = res[0].data;
        const goals = goalData.sort((a: any, b: any) => (a.completed_at < b.completed_at ? 1 : -1));
        goals.forEach((goal: any) => {
          if (goal?.completed_at) {
            const localDateTime = formatDateTimeToLocal(goal.completed_at);
            goal.completed_at_formatted = localDateTime.date;
          }
          if (goal?.created_at) {
            const localDateTime = formatDateTimeToLocal(goal.created_at);
            goal.created_at_formatted = localDateTime.date;
          }
        });

        //all goals outstanding at end of time range
        //that means they were created before end date and not yet completed
        let outstanding = goals?.filter((g: any) => !g.completed_at);
        outstanding = outstanding?.map((g: any) => {
          return { ...g, taskCount: `${g.completed_tasks}/${g.total_tasks}` };
        });
        //default to Goals outstanding data in table
        setActiveCard("Goals Outstanding");
        setSelectedData(outstanding);
        const tableHeaders = [
          { label: "Case ID", key: "case_id", hide: true },
          { label: "Case", key: "case_name", width: "300px" },
          { label: "Goal ID", key: "id", hide: true },
          { label: "Goal", key: "goal_name" },
          { label: "Name", key: "subject.name", width: "200px" },
          { label: "Tasks Completed", key: "taskCount", width: "120px" },
          { label: "Created", key: "created_at_formatted", width: "100px" },
        ];
        setDataHeaders(tableHeaders);
        return goals;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const getTasks = async (start: string, end: string) => {
    return fetchReportTasks(start, end)
      .then((res: any) => {
        const taskData = res[0].data;
        const tasks = taskData.sort((a: any, b: any) => (a.cm_reviewed_at < b.cm_reviewed_at ? 1 : -1));
        tasks.forEach((task: any) => {
          const localDateTime = formatDateTimeToLocal(task.cm_reviewed_at);
          task.cm_reviewed_at_formatted = localDateTime.date;
        });
        return tasks;
      })
      .catch((err: any) => {
        console.log(err);
        return null;
      });
  };

  const onClickDownloadReport = () => {
    reportRunning.value = true;
    downloadReport();
  };

  const downloadReport = async () => {
    const reportSection = document.getElementById("goals-section");
    const reportTitle = document.getElementById("goals-title");
    const refreshButton = document.getElementById("goals-refresh-button");
    const downloadGoalsButton = document.getElementById("download-goals-button");
    const downloadTasksButton = document.getElementById("download-tasks-button");

    if (reportSection) {
      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }
      if (refreshButton) refreshButton.style.visibility = "hidden";
      if (downloadGoalsButton) downloadGoalsButton.style.visibility = "hidden";
      if (downloadTasksButton) downloadTasksButton.style.visibility = "hidden";

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      await buildPDF(reportSection, "Goals and Tasks");
      reportRunning.value = false;

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }
      if (refreshButton) refreshButton.style.visibility = "visible";
      if (downloadGoalsButton) downloadGoalsButton.style.visibility = "visible";
      if (downloadTasksButton) downloadTasksButton.style.visibility = "visible";
    } else {
      console.error("Report section not found");
      reportRunning.value = false;
    }
  };

  const onClickCard = (cardId: string) => {
    setActiveCard((prev) => (prev === cardId ? "" : cardId));
  };

  return (
    <div id="goals-section">
      {isLoading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </div>
      ) : (
        <>
          <Row id="goals-title" className="mb-2">
            <Col className="col-8">
              <h4 className="hero-heading app-header-text">Goals and Tasks</h4>
            </Col>
            <Col className="text-end col-4">
              {!iPad && (
                <Button className="app-primary-color button" onClick={() => onClickDownloadReport()}>
                  <FontAwesomeIcon icon={faDownload} />
                  {" Report"}
                </Button>
              )}
            </Col>
          </Row>
          <div className="pdf-page">
            <Row>
              <Col>
                <ReportTimeInterval
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  dateInterval={dateInterval}
                  setDateInterval={setDateInterval}
                  intervalOne={30}
                  intervalOneText={"Last Month"}
                  intervalTwo={90}
                  intervalTwoText={"Last 3 Months"}
                  intervalThree={180}
                  intervalThreeText={"Last 6 Months"}
                  intervalFour={365}
                  intervalFourText={"Last Year"}
                  onClickRefresh={getData}
                  hideBtnId="goals-refresh-button"
                />
              </Col>
            </Row>
            <Row className="mt-3 mb-3">
              <Col className="col-3">
                <GoalsOutstandingMetric
                  data={data}
                  readOnly={false}
                  setSelectedData={setSelectedData}
                  setDataHeaders={setDataHeaders}
                  active={activeCard === "Goals Outstanding"}
                  onHandleClick={() => onClickCard("Goals Outstanding")}
                />
              </Col>
              <Col className="col-3">
                <GoalsCompletedMetric
                  data={data}
                  readOnly={false}
                  setSelectedData={setSelectedData}
                  setDataHeaders={setDataHeaders}
                  active={activeCard === "Goals Completed"}
                  onHandleClick={() => onClickCard("Goals Completed")}
                />
              </Col>
              <Col className="col-3">
                <TasksCompletedMetric
                  data={data}
                  readOnly={false}
                  setSelectedData={setSelectedData}
                  setDataHeaders={setDataHeaders}
                  active={activeCard === "Tasks Completed"}
                  onHandleClick={() => onClickCard("Tasks Completed")}
                />
              </Col>
              <Col className="col-3">
                <GoalsCompletedPercentageMetric
                  data={data}
                  readOnly={false}
                  setSelectedData={setSelectedData}
                  setDataHeaders={setDataHeaders}
                  active={activeCard === "Cases Completing Goals"}
                  onHandleClick={() => onClickCard("Cases Completing Goals")}
                />
              </Col>
            </Row>
          </div>
          <div className="pdf-page">
            {activeCard && selectedData && <DataTable tableHeadings={dataHeaders} tableData={selectedData} title={activeCard} />}
          </div>
        </>
      )}
    </div>
  );
};

export default GoalsAndTasksTab;
